import { CSSProperties, JSX } from 'preact/compat'
import { useState } from 'preact/hooks'
import ErrorTooltip from './ErrorTooltip'
import type { InputStyle } from './Input'

type Props = {
  options: InputStyle
  value: string
  name: string
  onChange: (value: string) => void
  onBlur?: (e: JSX.TargetedFocusEvent<HTMLTextAreaElement>) => void
  rows: number
  errorText?: string | null
  placeholder: string
}

const Textarea = (props: Props) => {
  const { options } = props
  const [focused, setFocused] = useState(false)

  const onChange = (e: JSX.TargetedEvent<HTMLTextAreaElement>) => {
    props.onChange(e.currentTarget.value)
  }

  const styles: CSSProperties = {
    position: 'relative'
  }

  const labelStyles: CSSProperties = {
    boxSizing: 'border-box',
    display: 'block',
    width: '100%',
    position: 'relative',
    letterSpacing: 0.45
  }

  const textareaStyles: CSSProperties = {
    width: '100%',
    padding: '24px 12px 0',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'none',
    fontSize: 14,
    border: `${options.border_size}px ${options.border_style} rgba(${options.border_color})`,
    borderTopLeftRadius: options.border_top_left,
    borderTopRightRadius: options.border_top_right,
    borderBottomRightRadius: options.border_bottom_left,
    borderBottomLeftRadius: options.border_bottom_right,
    backgroundColor: `rgba(${options.bg_color})`,
    color: `rgba(${options.text.text_color})`,
    fontFamily: options.text.font_family,
    fontWeight: options.text.font_weight
  }

  const spanStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 12,
    boxSizing: 'border-box',
    fontSize: 14,
    paddingTop: 18,
    paddingRight: 0,
    paddingBottom: 18,
    paddingLeft: 0,
    pointerEvents: 'none',
    color: `rgba(${options.label.text_color})`,
    fontFamily: options.label.font_family,
    fontWeight: options.label.font_weight,
    transition: 'font-size 200ms, padding 200ms',
    zIndex: 1
  }

  if (props.value || focused) {
    spanStyles.fontSize = 9
    spanStyles.paddingTop = 7
    spanStyles.textTransform = 'uppercase'
  }

  const onBlur = (e: JSX.TargetedFocusEvent<HTMLTextAreaElement>) => {
    setFocused(false)
    if (typeof props.onBlur === 'function') {
      props.onBlur(e)
    }
  }

  const onFocus = () => {
    setFocused(true)
  }

  return (
    <div className="wf_reset wf_textarea_wrapper" style={styles}>
      <label style={labelStyles} className="wf_reset wf_textarea_label">
        <textarea
          rows={props.rows || 3}
          name={props.name}
          style={textareaStyles}
          className={`wf_reset wf_textarea ${props.errorText ? 'wf_error' : ''}`}
          defaultValue={props.value}
          type="text"
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <span style={spanStyles} className="wf_reset wf_textarea_span">
          {props.placeholder}
        </span>
      </label>
      {props.errorText ? <ErrorTooltip text={props.errorText} /> : null}
    </div>
  )
}

export default Textarea
